import { ReactElement } from "react";
import { Table } from "react-bootstrap";

import {
    NullableNumber,
    ReportIndividualDataSingle,
    ReportItemType,
} from "types/Reports";

import { computeHorizontalBarLength } from "./utils/reportCompute";
import { formatValueAndUnitBasedOnTypeInReport } from "./utils/reportFormat";

import ReportTableRow from "./ReportTableRow";


type ReportTableProps = {
    headers: string[];

    itemType: ReportItemType;

    unit: string;
    colorClassName: string;
    dateFormat: string;

    data: ReportIndividualDataSingle[];

    maxValue: NullableNumber;
    minValue: NullableNumber;
};

export default function ReportTable({
    headers,
    itemType,
    data,
    unit,
    colorClassName,
    dateFormat,
    maxValue,
    minValue,
}: Readonly<ReportTableProps>) {
    const maxWidth = 250;
    const showAverage = itemType !== 'waterLeakage';

    const rows: ReactElement[] = data.map((item) => {
        const isValid = item.value !== null;
        const width = computeHorizontalBarLength(maxValue, minValue, item.value, maxWidth);

        let itemValueDisplay = `-- ${unit}`;
        let itemAverageDisplay = showAverage ? `-- ${unit}` : undefined;
        let itemDateDisplay = '--';

        if (isValid) {
            itemValueDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, item.value, unit, false);

            if (showAverage) {
                itemAverageDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, item.average, unit, false);
            }

            if (item.date) {
                itemDateDisplay = item.date.format(dateFormat);
            }
        }

        return <ReportTableRow
            key={`row-${item.id}-${item.name}`}
            id={`${item.id}`}
            name={item.name}
            value={itemValueDisplay}
            width={width}
            color={colorClassName}
            average={itemAverageDisplay}
            showAverage={showAverage}
            date={itemDateDisplay}
            isValid={isValid}
        />;
    });

    return (
        <Table className="report-table" responsive="sm" borderless hover>
            <thead>
                <tr className="report-table-header">
                    <th className="report-table-header--name">
                        <span className="report-table-header--name-text">{headers[0]}</span>
                    </th>
                    <th className="report-table-header--val" colSpan={2}>
                        {headers[1]}
                    </th>
                    <th className={`report-table-header--date`}>
                        <span className="report-table-header--date-overflow">{headers[2]}</span>
                    </th>
                    {showAverage && (
                        <th className="report-table-header--avg">
                            {headers[3]}
                        </th>
                    )}
                </tr>
            </thead>
            <tbody>
                {rows}
            </tbody>
        </Table>
    );
}